/**
 * Stable error codes. Spec 22.2.
 *
 * "Codes are stable identifiers. Clients switch on codes, never on messages — messages
 * are localised and change freely." Adding a code is cheap; renaming one is a breaking
 * change for every client that switches on it.
 */
export const ErrorCode = {
    // auth
    UNAUTHENTICATED: 'UNAUTHENTICATED',
    INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
    EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
    EMAIL_IN_USE: 'EMAIL_IN_USE',
    ACCOUNT_SUSPENDED: 'ACCOUNT_SUSPENDED',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    MFA_REQUIRED: 'MFA_REQUIRED',
    // authorization
    FORBIDDEN: 'FORBIDDEN',
    NOT_SESSION_HOST: 'NOT_SESSION_HOST',
    NOT_PARTICIPANT: 'NOT_PARTICIPANT',
    ROLE_REQUIRED: 'ROLE_REQUIRED',
    // session
    SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
    SESSION_FULL: 'SESSION_FULL',
    SESSION_CLOSED: 'SESSION_CLOSED',
    SESSION_NOT_STARTED: 'SESSION_NOT_STARTED',
    ALREADY_JOINED: 'ALREADY_JOINED',
    INVALID_SESSION_STATE: 'INVALID_SESSION_STATE',
    /**
     * §6.6: a joiner arrived after the session was locked to late joins. Distinct from
     * SESSION_CLOSED (which is terminal) — the session is live, the door is simply shut — so
     * the client shows "this session has begun" rather than an opaque failure.
     */
    SESSION_LOCKED: 'SESSION_LOCKED',
    /** §5.4: an AI-guided group was created above the participant cap. */
    AI_GROUP_TOO_LARGE: 'AI_GROUP_TOO_LARGE',
    /** §6.1: the facilitator already holds the maximum number of open practice sessions. */
    PRACTICE_LIMIT_REACHED: 'PRACTICE_LIMIT_REACHED',
    /**
     * SPEC DEVIATION: not in spec 22.2's list, but spec 8.2 gives it as an error of
     * `session.start` ("NO_PARTICIPANTS"), which genuinely needs it — starting an empty
     * session is a distinct, actionable failure, not a generic invalid state.
     *
     * Spec 8.2 also names `INVALID_STATE`, which is absent from 22.2. Treated as a spelling
     * of INVALID_SESSION_STATE rather than a separate code; 22.2 is authoritative.
     */
    NO_PARTICIPANTS: 'NO_PARTICIPANTS',
    // gameplay
    NOT_YOUR_TURN: 'NOT_YOUR_TURN',
    ALREADY_COMPLETED: 'ALREADY_COMPLETED',
    INTENTION_REQUIRED: 'INTENTION_REQUIRED',
    STALE_STATE: 'STALE_STATE',
    INVALID_POSITION: 'INVALID_POSITION',
    REFLECTION_NOT_EXPECTED: 'REFLECTION_NOT_EXPECTED',
    /**
     * SPEC DEVIATION: not in spec 22.2's list. ADR-0007 makes an unresolved landing block
     * the next roll — which spec 6.3's SKIP_REFLECTION command already implies, since a
     * command that advances past a reflection only means something if something was being
     * held back. The refusal needs a code of its own: a client that receives it must open
     * the reflection editor, which is a different response from any other invalid state.
     *
     * Added in the same spirit as NO_PARTICIPANTS above.
     */
    REFLECTION_REQUIRED: 'REFLECTION_REQUIRED',
    // billing
    NO_CREDITS: 'NO_CREDITS',
    SUBSCRIPTION_REQUIRED: 'SUBSCRIPTION_REQUIRED',
    PAYMENT_FAILED: 'PAYMENT_FAILED',
    PLAN_NOT_FOUND: 'PLAN_NOT_FOUND',
    /**
     * SPEC DEVIATION: not in spec 22.2's list. Amendment 001 defers Lemon Squeezy and gates
     * the whole payment surface on `BILLING_ENABLED`; while it is off, the checkout and
     * portal procedures and the webhook route have to refuse in a way a client can act on.
     * `NO_CREDITS` would be a lie — in preview mode everyone has unlimited access — and
     * `INTERNAL_ERROR` would send someone looking for a bug that is not there.
     */
    BILLING_DISABLED: 'BILLING_DISABLED',
    /**
     * SPEC DEVIATION: not in spec 22.2's list. A webhook whose HMAC does not verify is not
     * a `VALIDATION_ERROR` — nothing about the caller's *input* is malformed, and treating
     * it as one would file forged deliveries alongside ordinary user typos in the logs.
     * Spec 14.1 puts the payment provider outside the trust boundary; this is the code for
     * something claiming to be it and failing to prove it.
     */
    WEBHOOK_SIGNATURE_INVALID: 'WEBHOOK_SIGNATURE_INVALID',
    // mfa
    /**
     * SPEC DEVIATION: spec 22.2 lists only `MFA_REQUIRED`, which answers "you must verify".
     * Enrolment needs two more answers. `MFA_INVALID_CODE` is a business-rule failure a user
     * recovers from by retyping; a generic `VALIDATION_ERROR` would render as a form error on
     * a six-digit field that is perfectly well-formed.
     */
    MFA_INVALID_CODE: 'MFA_INVALID_CODE',
    /** Re-enrolling would silently invalidate the authenticator already in someone's pocket. */
    MFA_ALREADY_ENROLLED: 'MFA_ALREADY_ENROLLED',
    // content
    /**
     * SPEC DEVIATION: not in spec 22.2's list, and required by spec 11.4 — "the rule-set
     * simulator is not optional ... a rule set that makes square 68 unreachable would
     * otherwise ship silently". Activation refused because the dry run says the rule set
     * does not terminate or cannot reach its own goal. Distinct from `VALIDATION_ERROR`:
     * the config is structurally valid, which is exactly what makes it dangerous.
     */
    RULE_SET_UNSAFE: 'RULE_SET_UNSAFE',
    // ai
    AI_QUOTA_EXCEEDED: 'AI_QUOTA_EXCEEDED',
    AI_SESSION_LIMIT_REACHED: 'AI_SESSION_LIMIT_REACHED',
    /** The trialing subscriber's lifetime AI spend cap is reached; convert to a paid plan to lift it. */
    AI_TRIAL_LIMIT_REACHED: 'AI_TRIAL_LIMIT_REACHED',
    AI_UNAVAILABLE: 'AI_UNAVAILABLE',
    AI_TIMEOUT: 'AI_TIMEOUT',
    // audio (chat/audio feature)
    /** Audio is off for this deployment/session, or LiveKit could not mint a token. The client
     *  degrades to the external call link — audio is never required to play. */
    AUDIO_UNAVAILABLE: 'AUDIO_UNAVAILABLE',
    /** The audio room is at AUDIO_MAX_PARTICIPANTS. */
    AUDIO_ROOM_FULL: 'AUDIO_ROOM_FULL',
    // generic
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    RATE_LIMITED: 'RATE_LIMITED',
    NOT_FOUND: 'NOT_FOUND',
    CONFLICT: 'CONFLICT',
    INTERNAL_ERROR: 'INTERNAL_ERROR',
};
//# sourceMappingURL=codes.js.map